import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";

import { getElement, getPage, getWordDefinition } from "@courses-pm2/api/src/ts/methods";
import { ContentModel, ElementModel } from "@courses-pm2/api/src/ts/models";

/**
 * Specification of the signature for an event listener, which is a method used
 * to provide an exchange of events between clients and the components that are
 * rendered through this engine if they have no direct link to each other.
 */
type EventListener = () => void;

/**
 * Shorthand typing for an ElementModel constructor method, so we can easily use
 * static references in our typings.
 */
// tslint:disable-next-line: no-any
type ElementModelCtor<M extends ElementModel = ElementModel> = new (...args: any[]) => M;

/**
 * A renderer is a method provided by embedding clients, which will transform an
 * ElementModel into a React node which can be mounted into the DOM.
 */
type Renderer<T extends {}, M extends ElementModel = ElementModel> = (args: {
    engine: Engine<T>;
    model: M;
    props: Partial<T>;
}) => React.ReactNode;

/**
 * The mapper is used to provide a mechanism to resolve renderers for different
 * kinds of ElementModels as they are being announced by the content parser
 * (this way we can postload components as needed).
 */
type ComponentMapper<T extends {} = {}, M extends ElementModel = ElementModel> = (
    (model: ElementModelCtor<M>) => Promise<Renderer<T, M> | undefined> | Renderer<T, M> | undefined
);

/**
 * The Engine provides a renderering mechanism, through which the embedding
 * application can associate components with ElementModels and also allows an
 * exchange of events between client and components.
 */
export class Engine<T extends {} = {}> {
    /**
     * Specifies if the engine is configured to run in preview mode (ie.
     * fetching content with the preview header enabled).
     */
    public readonly previewMode: boolean;

    /**
     * Will contain a reference to the user, on whose behalf this engine is
     * rendering content.
     */
    public readonly user?: AuthenticatedUserModel;

    /**
     * Internal cache of event listeners currently subscribed to different
     * events.
     */
    protected readonly eventListeners: { [evt: string]: Set<EventListener> | undefined } = {};

    /**
     * Will optionally contain a reference to the mapper method, so we can
     * resolve components to render different kinds of data as they are being
     * found by the content parser.
     */
    protected readonly mapper?: ComponentMapper<T>;

    /**
     * Internal map that's used to cache references to the renderers used to
     * transform different ElementModels to React nodes, so we don't have to
     * fetch the same renderers over and over.
     */
    protected readonly renderers: WeakMap<ElementModelCtor, Renderer<T> | undefined> = new WeakMap();

    /**
     * Internal set that's used to keep track of promises returned by the
     * mapper, which we're currently waiting for.
     */
    protected readonly promises: Set<Promise<Renderer<T> | undefined>> = new Set();

    /**
     * Cache reference to the mapper method when the engine is being
     * instantiated, so that we can refer to it later on.
     */
    constructor(initial: {
        mapper?: ComponentMapper<T>;
        user?: AuthenticatedUserModel;
        previewMode?: boolean;
    } = {}) {
        this.mapper = initial.mapper;
        this.user = initial.user;
        this.previewMode = initial.previewMode === true;
    }

    /**
     * Wrapper around the getElement method, which will make sure that this
     * engine instance is going to be propagated through to the ElementModels.
     */
    public async getElement(args: {
        elementId: string;
        portalId: string;
    }): Promise<ContentModel> {
        // Wait for the model to be fetched
        const model = await getElement({
            ...args,
            previewMode: this.previewMode,
            user: this.user,
            engine: <Engine><unknown>this,
        });

        // Now wait for all promises returned by the mapper to be resolved, so
        // we know that the model is ready to render
        await Promise.all([...this.promises]);

        // Finally return the model, ready for rendering
        return model;
    }

    /**
     * Wrapper around the getPage method, which will make sure that this
     * engine instance is going to be propagated through to the ElementModels.
     */
    public async getPage(args: {
        pageId: string;
        portalId: string;
    }): Promise<ContentModel> {
        // Wait for the model to be fetched
        const model = await getPage({
            ...args,
            previewMode: this.previewMode,
            user: this.user,
            engine: <Engine><unknown>this,
        });

        // Now wait for all promises returned by the mapper to be resolved, so
        // we know that the model is ready to render
        await Promise.all([...this.promises]);

        // Finally return the model, ready for rendering
        return model;
    }

    /**
     * Wrapper around the getWordDefinition method, which will make sure that
     * the configurations from this engine instance will be propagated to the
     * remote backend.
     */
    public async getWordDefinition(args: {
        wordId: string;
        portalId: string;
    }): Promise<React.ReactNode> {
        // Wait for the model to be fetched
        return getWordDefinition({
            ...args,
            previewMode: this.previewMode,
            user: this.user,
            engine: this,
        });
    }

    /**
     * Helper that allows statically associating a renderer with a specific kind
     * of ElementModel without waiting for the mapper to resolve it.
     */
    public map<M extends ElementModel>(model: ElementModelCtor<M>, renderer: Renderer<T, M>): void {
        // tslint:disable-next-line: no-any
        this.renderers.set(model, <any>renderer);
    }

    /**
     * This method is triggered internally by @courses-pm2/api by the content
     * parser whenever a new ElementModel is being created. Do not trigger the
     * method manually unless you know what you are doing.
     *
     * Behind the scenes, this is where we're using the component mapper to
     * fetch components to render models as they've been found by the parser.
     */
    public announce<M extends ElementModel>(model: ElementModelCtor<M>): void {
        // Bail out if the mapper method wasn't given
        if (!this.mapper) {
            return;
        }

        // If no parser has been fetched yet, then bail out
        if (this.renderers.has(model)) {
            return;
        }

        // ... Otherwise begin fetching the renderer for this model now
        const promise = Promise.resolve(this.mapper(model));

        // Push to the list of currently running renderer promises, so that the
        // engine is aware that we're waiting for the mapper to respond
        this.promises.add(promise);

        // Handle response from the mapper
        promise.then(
            (renderer) => {
                // If the renderer was found, then cache it for future use
                this.renderers.set(model, renderer);

                // Remove from the list of promises we're waiting for
                this.promises.delete(promise);
            },

            (err) => {
                // If an error was thrown, then propagate it to the console
                // without breaking the render chain all together
                console.error(err); // tslint:disable-line no-console

                // Remove from the list of promises we're waiting for
                this.promises.delete(promise);
            },
        );
    }

    /**
     * utility to wait for all promises created by the engine to resolve
     */
    public async waitForPromises(): Promise<void> {
        await Promise.all([...this.promises]);
    }

    /**
     * Helper that'll trigger the renderer associated with the given model,
     * turning it into a React node that's ready for mounting.
     */
    public render(model: ElementModel, props?: T): React.ReactNode {
        const renderer = this.renderers.get(<ElementModelCtor>model.constructor);

        // Bail out if no renderer was associated with this model
        if (!renderer) {
            if (process.env.NODE_ENV !== "production") {
                console.warn(`Engine.render(...): No renderer associated with the following element:`, model);
            }

            return undefined;
        }

        // ... Otherwise actually render the content
        return renderer({
            engine: this,
            model,
            props: props || {},
        });
    }

    /**
     * Subscribes the listener to the specified event.
     */
    public on(evt: string, listener: EventListener): void {
        this.getListeners(evt)
            .add(listener);
    }

    /**
     * Subscribes the listener to the next time the given event is fired.
     */
    public once(evt: string, listener: EventListener): void {
        // Create a wrapped listener, that'll remove itself instantly after
        // being triggered the first time
        const wrappedListener = () => {
            // Fire the actual listener
            listener();

            // Unsubscribe immediately
            this.off(evt, wrappedListener);
        };

        // Subscribe the wrapped listener so it'll be triggered next time the
        // event is fired
        this.on(evt, wrappedListener);
    }

    /**
     * Subscribes the listener to the specified event.
     */
    public off(evt: string, listener: EventListener): void {
        this.getListeners(evt)
            .delete(listener);
    }

    /**
     * Emits the specified event, making sure that all associated listeners will
     * be triggered.
     */
    public emit(evt: string): void {
        this.getListeners(evt)
            .forEach((listener) => { listener(); });
    }

    /**
     * Helper that'll return a reference to the set of listeners associated with
     * the given event (if the set doesn't exist yet, a new one is created).
     */
    protected getListeners(evt: string): Set<EventListener> {
        return this.eventListeners[evt] = this.eventListeners[evt] || new Set();
    }
}
